import { computed, DestroyRef, Injectable, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BetType, BetTypeKey } from '../models/bet-type.model';
import { Match } from '../models/match.model';
import { Odd } from '../models/odd.model';
import { SessionService } from '../session.service';
import { DEFAULT_SPORT_ORDER, getSportPresentation } from './config/sportsbook-sports.config';
import { MATCH_FILTER_OPTIONS } from './config/sportsbook-filters.config';
import {
  MATCH_DETAIL_SECTION_FAMILIES,
  getMatchDetailTabIdsForBetTypeKey,
  getMatchDetailTabs,
  shouldIncludeUnconfiguredMatchDetailBetTypes
} from './config/sportsbook-match-detail.config';
import { QUICK_SELECTOR_DEFINITIONS, getQuickSelectorPreset } from './config/sportsbook-quick-selectors.config';
import { type QuickSelectorDefinition, type SportQuickSelectorPreset } from './config/sportsbook-config.types';
import {
  BetSlipGroupViewModel,
  BetSlipSelectionViewModel,
  MatchDetailOptionViewModel,
  MatchDetailSectionViewModel,
  MatchDetailTabId,
  MatchDetailViewModel,
  MatchFilterId,
  MatchFilterOption,
  MatchPreviewViewModel,
  SelectionToggleRequest,
  QuickSelectionViewModel,
  SportMenuItem,
  TournamentGroupViewModel
} from './sportsbook.models';
import {
  buildMatchName,
  getSectionTitleFallback,
  localizeSectionTitle,
  localizeTabLabel,
  resolveAwayTeamLabel,
  resolveBetTypeLabel,
  resolveCanonicalBetTypeLabel,
  resolveHomeTeamLabel,
  resolveOutrightLabel,
  resolveSportLabel,
  resolveStaticLabel,
  type SportsbookLocalizationContext
} from './facade/sportsbook-localization.utils';
import {
  buildDetailMarketKey,
  buildSelectionMarketKey,
  compareBetTypes,
  getSpecialValue,
  resolveDetailOddLabel,
  resolveHeaderLabel,
  resolveMatchDetailLayout,
  resolveSelectionLabel
} from './facade/sportsbook-market.utils';
import {
  buildMetaLines,
  formatKickoffTime,
  isBettableMatch,
  isDisplayableMatch,
  isLiveMatch,
  matchesFilter,
  matchesSearchQuery,
  parseScore,
  resolveCountryFlag
} from './facade/sportsbook-match.utils';
import { tokenizeSearchText, trimToNull } from './facade/sportsbook-shared.utils';
import { merge, Observable } from 'rxjs';
import { AuthDialogService } from '../auth-dialog.service';
import { ToastService } from '../toast/toast.service';
import { BetSlipTicketService } from '../betslip/betslip-ticket.service';
import {
  BET_SLIP_IDLE_SUBMISSION_STATE,
  BetSlipMode,
  BetSlipSubmissionState
} from '../betslip/betslip-ticket.models';
import {
  buildBetSlipBonusHint,
  buildBetSlipSummary,
  buildBetSlipTicketPayload,
  clampSystemX
} from '../betslip/betslip-ticket.utils';

interface StoredSelection {
  matchId: number;
  betTypeId: number;
  oddId: number;
}

interface ResolvedSelectionProjection {
  value: number;
  marketLabel: string;
  selectionLabel: string;
}

interface StoredOddProjection {
  betType: BetType;
  odd: Odd;
}

interface ProjectedOutrightOdd {
  betType: BetType;
  odd: Odd;
  label: string | null;
}

interface ResolvedBetSlipSelection extends BetSlipSelectionViewModel {
  tournamentId: number;
  matchId: number;
  matchName: string;
  competitors: string;
  matchStartDate: string;
  matchIdentifier: string;
  matchNumber: number;
  sportId: number;
  matchActive: boolean;
  isLive: boolean;
  isOutright: boolean;
  minCombination: number;
  isH2H: boolean;
  homeTeamBrUniqueId: number;
  awayTeamBrUniqueId: number;
}

interface TournamentGroupEntry {
  id: number;
  sportId: number;
  country: string;
  countryCode: string | null;
  name: string;
  categorySortIndex: number;
  tournamentSortIndex: number;
  matches: readonly Match[];
}

const OUTRIGHT_QUICK_SELECTION_LIMIT = 6;
const OUTRIGHT_QUICK_SELECTION_MARKET_KEY = 'outright-winner';

@Injectable({ providedIn: 'root' })
export class SportsbookFacadeService {
  private readonly session = inject(SessionService);
  private readonly translate = inject(TranslateService, { optional: true });
  private readonly authDialog = inject(AuthDialogService, { optional: true });
  private readonly toast = inject(ToastService, { optional: true });
  private readonly betSlipTicketService = inject(BetSlipTicketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly collapsedTournamentIds = signal(new Set<number>());
  private readonly selectedQuickGroupIdsBySport = signal<ReadonlyMap<number, string>>(new Map());
  private readonly selectedSelections = signal(new Map<string, StoredSelection>());
  private readonly hasFilterInteraction = signal(false);
  private readonly selectedFilter = signal<MatchFilterId>('all');
  private readonly searchExpandedState = signal(false);
  private readonly translationRevision = signal(0);
  private readonly orderedSportIds = computed<number[]>(() => {
    const actualSportIds = Array.from(this.session.sports().keys()).filter(id => id > 0);
    return actualSportIds
      .filter((sportId, index, current) => current.indexOf(sportId) === index)
      .sort((left, right) => this.getSportOrder(left) - this.getSportOrder(right));
  });
  private readonly visibleSportIds = computed<ReadonlySet<number>>(() => {
    const selectedSports = this.session.selectedSports();
    return selectedSports.size ? selectedSports : new Set(this.orderedSportIds());
  });

  private readonly filterOptionsState = computed<readonly MatchFilterOption[]>(() => {
    const counts = new Map<MatchFilterId, number>(MATCH_FILTER_OPTIONS.map(option => [option.id, 0]));

    for (const match of this.session.matches().values()) {
      if (!this.matchesCurrentScope(match)) {
        continue;
      }

      for (const option of MATCH_FILTER_OPTIONS) {
        if (!matchesFilter(match, option.id, {
          isDisplayableMatch,
          isLiveMatch
        })) {
          continue;
        }

        counts.set(option.id, (counts.get(option.id) ?? 0) + 1);
      }
    }

    return MATCH_FILTER_OPTIONS.map(option => ({
      ...option,
      count: counts.get(option.id) ?? 0
    }));
  });
  readonly activeFilter = computed<MatchFilterId>(() => {
    if (this.hasFilterInteraction()) {
      return this.selectedFilter();
    }

    return this.hasDisplayableLiveMatches() ? 'live' : 'all';
  });
  readonly sportsMenuExpanded = signal(true);
  readonly betSlipExpanded = signal(true);
  readonly stakeAmount = signal(10);
  readonly betSlipMode = signal<BetSlipMode>('combi');
  readonly betSlipSystemX = signal(2);
  readonly betSlipSubmissionState = signal<BetSlipSubmissionState>(BET_SLIP_IDLE_SUBMISSION_STATE);
  readonly maxWinPulseKey = signal(0);
  readonly searchQuery = signal('');
  readonly searchExpanded = computed(() => this.searchExpandedState() || this.searchQuery().trim().length > 0);
  private readonly normalizedSearchTokens = computed(() => tokenizeSearchText(this.searchQuery()));

  constructor() {
    if (!this.translate) {
      return;
    }

    const translationStreams = [this.translate.onLangChange, this.translate.onTranslationChange]
      .filter(Boolean) as Observable<unknown>[];

    if (!translationStreams.length) {
      return;
    }

    merge(...translationStreams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.translationRevision.update(current => current + 1);
      });
  }

  get filterOptions(): readonly MatchFilterOption[] {
    return this.filterOptionsState();
  }

  hasVisibleSport(sportId: number): boolean {
    return this.tournamentGroupEntries().some(group => group.sportId === sportId);
  }

  getTournamentGroups(limit = Number.MAX_SAFE_INTEGER): TournamentGroupViewModel[] {
    const entries = this.tournamentGroupEntries();
    const visibleEntries = limit >= entries.length ? entries : entries.slice(0, limit);

    return visibleEntries.map(entry => this.materializeTournamentGroup(entry));
  }

  readonly sports = computed<SportMenuItem[]>(() => {
    this.translationRevision();
    const translate = this.t.bind(this);
    const counts = new Map<number, { total: number; live: number }>();

    for (const match of this.session.matches().values()) {
      if (!isDisplayableMatch(match)) {
        continue;
      }

      const current = counts.get(match.Sport) ?? { total: 0, live: 0 };
      counts.set(match.Sport, {
        total: current.total + 1,
        live: current.live + (isLiveMatch(match) ? 1 : 0)
      });
    }

    const sportIds = this.orderedSportIds();
    const selectedSports = this.session.selectedSports();

    return sportIds
      .map(sportId => {
        const presentation = getSportPresentation(sportId);
        return {
          id: sportId,
          label: resolveSportLabel(sportId, translate),
          iconName: presentation.iconName,
          liveCount: counts.get(sportId)?.live ?? 0,
          totalCount: counts.get(sportId)?.total ?? 0,
          selected: selectedSports.has(sportId)
        } satisfies SportMenuItem;
      });
  });

  private readonly tournamentGroupEntries = computed<readonly TournamentGroupEntry[]>(() => {
    this.translationRevision();
    const groupedMatches = new Map<number, Match[]>();

    for (const match of this.session.matches().values()) {
      if (!this.matchesCurrentView(match)) {
        continue;
      }

      const current = groupedMatches.get(match.LB_TournamentID) ?? [];
      current.push(match);
      groupedMatches.set(match.LB_TournamentID, current);
    }

    return Array.from(groupedMatches.entries())
      .map(([tournamentId, matches]) => {
        const firstMatch = matches[0];

        return {
          id: tournamentId,
          sportId: firstMatch.Sport,
          country: firstMatch.Category,
          name: firstMatch.Tournament,
          countryCode: firstMatch.CountryCode,
          categorySortIndex: firstMatch.LB_CategorySortIndex,
          tournamentSortIndex: firstMatch.LB_TournamentSortIndex,
          matches
        } satisfies TournamentGroupEntry;
      })
      .sort((left, right) => {
        return (
          left.categorySortIndex - right.categorySortIndex ||
          left.tournamentSortIndex - right.tournamentSortIndex ||
          left.name.localeCompare(right.name)
        );
      });
  });

  readonly tournamentGroupCount = computed(() => this.tournamentGroupEntries().length);

  readonly tournamentGroups = computed<TournamentGroupViewModel[]>(() => this.getTournamentGroups());

  private readonly betSlipSelections = computed<readonly ResolvedBetSlipSelection[]>(() => {
    const currentLanguage = this.session.currentLanguage();

    return Array.from(this.selectedSelections().values()).flatMap(storedSelection => {
      const match = this.session.matches().get(storedSelection.matchId);
      if (!match) {
        return [];
      }

      const currentSelection = this.resolveBetSlipSelection(
        match,
        storedSelection,
        currentLanguage
      );

      return currentSelection ? [currentSelection] : [];
    });
  });

  readonly betSlipGroups = computed<BetSlipGroupViewModel[]>(() => {
    const groups = new Map<number, BetSlipGroupViewModel>();

    for (const selection of this.betSlipSelections()) {
      const currentGroup = groups.get(selection.matchId) ?? {
        tournamentId: selection.tournamentId,
        matchId: selection.matchId,
        matchName: selection.matchName,
        competitors: selection.competitors,
        matchStartDate: selection.matchStartDate,
        matchIdentifier: selection.matchIdentifier,
        matchNumber: selection.matchNumber,
        sportId: selection.sportId,
        active: selection.matchActive,
        isLive: selection.isLive,
        isOutright: selection.isOutright,
        minCombination: selection.minCombination,
        isH2H: selection.isH2H,
        homeTeamBrUniqueId: selection.homeTeamBrUniqueId,
        awayTeamBrUniqueId: selection.awayTeamBrUniqueId,
        selections: []
      };

      (currentGroup.selections as BetSlipSelectionViewModel[]).push({
        key: selection.key,
        betTypeId: selection.betTypeId,
        betTypeKey: selection.betTypeKey,
        marketLabel: selection.marketLabel,
        selectionLabel: selection.selectionLabel,
        oddId: selection.oddId,
        oddTypeKey: selection.oddTypeKey,
        oddFieldType: selection.oddFieldType,
        specialOddsValue: selection.specialOddsValue,
        odds: selection.odds,
        active: selection.active
      });

      groups.set(selection.matchId, currentGroup);
    }

    return Array.from(groups.values())
      .sort((left, right) => left.matchName.localeCompare(right.matchName));
  });

  readonly selectedOddsCount = computed(() => this.betSlipSelections().length);

  readonly betSlipSummary = computed(() => {
    const settings = this.session.bettingSettings();

    return buildBetSlipSummary(this.betSlipGroups(), {
      mode: this.betSlipMode(),
      systemX: this.betSlipSystemX(),
      stake: this.stakeAmount(),
      balance: this.session.currentUser()?.Credit ?? null,
      ...(settings ?? {})
    });
  });

  readonly betSlipBonusHint = computed(() => buildBetSlipBonusHint(
    this.betSlipSummary(), this.session.bettingSettings()?.combiBonus ?? []
  ));

  readonly totalOddsMultiplier = computed(() => {
    return this.betSlipSummary().totalOdds;
  });

  readonly maxWin = computed(() => this.betSlipSummary().maxWin);

  getMatchDetail(matchId: number): MatchDetailViewModel | null {
    this.translationRevision();
    const currentLanguage = this.session.currentLanguage();
    const translate = this.t.bind(this);
    const match = this.session.matches().get(matchId);
    if (!match || !isDisplayableMatch(match)) {
      return null;
    }

    const metaLines = buildMetaLines(match, {
      isLiveMatch,
      formatKickoffTime
    });
    const livePrimaryMeta = metaLines[0] ?? '';
    const liveSecondaryMeta = metaLines[1] ?? '';

    const sections = this.buildMatchDetailSections(match);
    const tabCounts = new Map<MatchDetailTabId, number>();

    for (const section of sections) {
      for (const tabId of section.tabIds) {
        tabCounts.set(tabId, (tabCounts.get(tabId) ?? 0) + 1);
      }
    }

    const tabs = getMatchDetailTabs(tabCounts)
      .map(tab => ({
        ...tab,
        label: localizeTabLabel(tab.id, tab.label, translate)
      }));

    const matchIsLive = isLiveMatch(match);

    return {
      id: match.Id,
      tournamentId: match.LB_TournamentID,
      sportId: match.Sport,
      sportLabel: resolveSportLabel(match.Sport, translate),
      tournamentName: match.Tournament,
      categoryLabel: match.Category,
      homeTeam: resolveHomeTeamLabel(match, currentLanguage),
      awayTeam: resolveAwayTeamLabel(match, currentLanguage),
      isOutright: match.Outright,
      outrightLabel: match.Outright ? resolveOutrightLabel(match, currentLanguage) : null,
      scheduledAt: match.Date,
      isLive: matchIsLive,
      heroBadge: matchIsLive
        ? (livePrimaryMeta ? `${livePrimaryMeta} ${translate('sportsbook.common.live')}` : translate('sportsbook.common.live'))
        : null,
      heroMeta: matchIsLive ? liveSecondaryMeta : formatKickoffTime(match.Date),
      scoreLines: parseScore(match.Score),
      tabs,
      sections
    };
  }

  setActiveFilter(filterId: MatchFilterId): void {
    this.hasFilterInteraction.set(true);
    this.selectedFilter.set(filterId);
  }

  toggleSearch(): void {
    if (this.searchExpanded() && this.searchQuery().trim().length > 0) {
      this.searchQuery.set('');
      this.searchExpandedState.set(false);
      return;
    }

    this.searchExpandedState.update(current => !current);
  }

  setSearchQuery(query: string): void {
    this.searchQuery.set(query);

    if (query.trim().length > 0) {
      this.searchExpandedState.set(true);
    }
  }

  toggleSport(sportId: number): void {
    this.session.toggleSportSelection(sportId);
  }

  getSelectedQuickGroupId(sportId: number): string | null {
    return this.selectedQuickGroupIdsBySport().get(sportId) ?? null;
  }

  setSelectedQuickGroupId(sportId: number, quickGroupId: string): void {
    this.selectedQuickGroupIdsBySport.update(current => {
      if (current.get(sportId) === quickGroupId) {
        return current;
      }

      const next = new Map(current);
      next.set(sportId, quickGroupId);
      return next;
    });
  }

  setDesktopSidePanelsExpanded(expanded: boolean): void {
    this.sportsMenuExpanded.set(expanded);
    this.betSlipExpanded.set(expanded);
  }

  toggleSportsMenu(): void {
    this.sportsMenuExpanded.update(current => !current);
  }

  toggleBetSlip(): void {
    this.betSlipExpanded.update(current => !current);
  }

  setBetSlipMode(mode: BetSlipMode): void {
    const summary = this.betSlipSummary();
    if (mode === 'system' && !summary.canUseSystem) {
      return;
    }

    this.betSlipMode.set(mode);
    if (mode === 'system') {
      this.betSlipSystemX.set(clampSystemX(Math.max(summary.matchCount - 1, 2), summary.matchCount));
    }
    this.resetBetSlipSubmissionState();
  }

  stepBetSlipSystemX(delta: number): void {
    const summary = this.betSlipSummary();
    if (!summary.canUseSystem) {
      return;
    }

    this.betSlipMode.set('system');
    this.betSlipSystemX.set(clampSystemX(summary.systemX + delta, summary.matchCount));
    this.resetBetSlipSubmissionState();
  }

  clearBetSlip(): void {
    if (!this.selectedOddsCount()) {
      return;
    }

    this.selectedSelections.set(new Map());
    this.betSlipMode.set('combi');
    this.betSlipSystemX.set(2);
    this.resetBetSlipSubmissionState();
    this.pulseMaxWin();
  }

  toggleTournament(tournamentId: number): void {
    this.collapsedTournamentIds.update(current => {
      const next = new Set(current);
      if (next.has(tournamentId)) {
        next.delete(tournamentId);
      } else {
        next.add(tournamentId);
      }
      return next;
    });
  }

  toggleSelection(action: SelectionToggleRequest): void {
    const selectionKey = storedSelectionKey(action.matchId, action.betTypeId);

    this.selectedSelections.update(current => {
      const next = new Map(current);
      const existing = next.get(selectionKey);

      if (existing?.oddId === action.oddId) {
        next.delete(selectionKey);
        this.resetBetSlipSubmissionState();
        this.pulseMaxWin();
        return next;
      }

      if (!this.hasStoredSelection(action)) {
        return current;
      }

      next.set(selectionKey, {
        matchId: action.matchId,
        betTypeId: action.betTypeId,
        oddId: action.oddId
      });

      this.resetBetSlipSubmissionState();
      this.pulseMaxWin();
      return next;
    });
  }

  /** Adds replay selections without toggling off or replacing an existing market selection. */
  addSelections(actions: readonly SelectionToggleRequest[]): number {
    let added = 0;
    this.selectedSelections.update(current => {
      const next = new Map(current);
      for (const action of actions) {
        const key = storedSelectionKey(action.matchId, action.betTypeId);
        if (!next.has(key) && this.hasStoredSelection(action)) {
          next.set(key, { ...action });
          added++;
        }
      }
      return added ? next : current;
    });
    if (added) {
      this.resetBetSlipSubmissionState();
      this.pulseMaxWin();
      this.betSlipExpanded.set(true);
    }
    return added;
  }

  updateStake(value: number): void {
    this.stakeAmount.set(Math.floor(Math.max(value, 0) * 100) / 100);
    this.resetBetSlipSubmissionState();
    this.pulseMaxWin();
  }

  placeBet(): void {
    const summary = this.betSlipSummary();
    if (!summary.selectionCount) {
      return;
    }

    if (summary.requiresLogin) {
      this.authDialog?.openLogin();
      this.betSlipSubmissionState.set({
        kind: 'idle',
        messageKey: 'betslip.messages.loginRequired'
      });
      return;
    }

    if (!summary.canSubmit) {
      this.betSlipSubmissionState.set({
        kind: 'idle',
        messageKey: summary.blockingMessageKey
      });
      return;
    }

    const user = this.session.currentUser();
    if (!user) {
      return;
    }

    const payload = buildBetSlipTicketPayload(this.betSlipGroups(), summary, user.LB2SO);
    this.betSlipSubmissionState.set({
      kind: 'submitting',
      messageKey: 'betslip.messages.submitting'
    });

    this.betSlipTicketService.placeTicket(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (response.Success) {
            this.selectedSelections.set(new Map());
            this.betSlipMode.set('combi');
            this.betSlipSystemX.set(2);
            this.betSlipSubmissionState.set({
              kind: 'accepted',
              messageKey: response.ReservationCode ? 'betslip.messages.acceptedWithCode' : 'betslip.messages.accepted',
              messageParams: response.ReservationCode ? { code: response.ReservationCode } : undefined
            });
            this.toast?.showSuccess('betslip.messages.accepted');
            this.pulseMaxWin();
            return;
          }

          this.betSlipSubmissionState.set({
            kind: 'declined',
            messageKey: 'betslip.messages.declined'
          });
          this.toast?.showError('betslip.messages.declined');
        },
        error: () => {
          this.betSlipSubmissionState.set({
            kind: 'error',
            messageKey: 'betslip.messages.submitError'
          });
          this.toast?.showError('betslip.messages.submitError');
        }
      });
  }

  private buildMatchPreview(match: Match): MatchPreviewViewModel {
    const currentLanguage = this.session.currentLanguage();
    const quickSelections = this.buildQuickSelections(match);
    const matchIsLive = isLiveMatch(match);

    return {
      id: match.Id,
      tournamentId: match.LB_TournamentID,
      isOutright: match.Outright,
      outrightLabel: match.Outright ? resolveOutrightLabel(match, currentLanguage) : null,
      homeTeam: resolveHomeTeamLabel(match, currentLanguage),
      awayTeam: resolveAwayTeamLabel(match, currentLanguage),
      isLive: matchIsLive,
      statusTag: matchIsLive ? this.t('sportsbook.common.live') : null,
      metaLines: buildMetaLines(match, { isLiveMatch, formatKickoffTime }),
      scoreLines: parseScore(match.Score),
      quickSelections,
      moreMarketsCount: Math.max(match.BetTypeCount, 0)
    };
  }

  private materializeTournamentGroup(entry: TournamentGroupEntry): TournamentGroupViewModel {
    const translate = this.t.bind(this);
    const sortedMatches = [...entry.matches].sort((left, right) => {
      return (
        Number(isLiveMatch(right)) - Number(isLiveMatch(left)) ||
        new Date(left.Date).getTime() - new Date(right.Date).getTime() ||
        left.MatchNumber - right.MatchNumber
      );
    });
    const matchViewModels = sortedMatches.map(match => this.buildMatchPreview(match));

    return {
      id: entry.id,
      sportId: entry.sportId,
      sportLabel: resolveSportLabel(entry.sportId, translate),
      country: entry.country,
      countryFlag: resolveCountryFlag(entry.countryCode),
      name: entry.name,
      expanded: !this.collapsedTournamentIds().has(entry.id),
      matchCount: entry.matches.length,
      quickSelectionGroups: buildQuickSelectionGroups(matchViewModels[0]?.quickSelections ?? []),
      matches: matchViewModels
    };
  }

  private buildQuickSelections(match: Match): QuickSelectionViewModel[] {
    if (match.Outright) {
      return this.buildOutrightQuickSelections(match);
    }

    return getQuickSelectorPreset(match.Sport, isLiveMatch(match)).flatMap(preset => this.resolveQuickSelectorGroup(match, preset));
  }

  private buildOutrightQuickSelections(match: Match): QuickSelectionViewModel[] {
    const currentLanguage = this.session.currentLanguage();
    const translate = this.t.bind(this);
    const localizationContext: SportsbookLocalizationContext = { currentLanguage, t: translate };
    const projectedOdds = match.BetTypes
      .filter(betType => betType.Active && betType.Key === BetTypeKey.OUTRIGHT_WINNER)
      .flatMap(betType => betType.Odds
        .filter(odd => odd.Active)
        .map(odd => ({
          betType,
          odd,
          label: resolveDetailOddLabel(match, betType, odd, localizationContext)
        }) satisfies ProjectedOutrightOdd))
      .sort((left, right) => left.odd.Value - right.odd.Value || left.odd.Id - right.odd.Id)
      .slice(0, OUTRIGHT_QUICK_SELECTION_LIMIT);

    const betTypeId = projectedOdds[0]?.betType.Id ?? null;
    const marketKey = buildSelectionMarketKey(projectedOdds[0]?.betType, OUTRIGHT_QUICK_SELECTION_MARKET_KEY);
    const selectedOddId = this.getSelectedOddId(match.Id, betTypeId);

    return Array.from({ length: OUTRIGHT_QUICK_SELECTION_LIMIT }, (_, index) => {
      const projectedOdd = projectedOdds[index] ?? null;
      const headerLabel = `${translate('sportsbook.tabs.top')} ${index + 1}`;

      return {
        key: `${match.Id}:${marketKey}:${index}`,
        quickGroupId: OUTRIGHT_QUICK_SELECTION_MARKET_KEY,
        betTypeId,
        marketKey,
        marketLabel: resolveCanonicalBetTypeLabel(
          BetTypeKey.OUTRIGHT_WINNER,
          getSectionTitleFallback('outright-winner') ?? '',
          translate
        ),
        headerLabel,
        selectionLabel: projectedOdd?.label ?? '',
        oddId: projectedOdd?.odd.Id ?? null,
        value: projectedOdd?.odd.Value ?? null,
        available: projectedOdd !== null
          && isBettableMatch(match)
          && !projectedOdd.betType.LimitHit
          && projectedOdd.odd.Value > 1,
        selected: projectedOdd?.odd.Id === selectedOddId
      } satisfies QuickSelectionViewModel;
    });
  }

  private resolveQuickSelectorGroup(match: Match, preset: SportQuickSelectorPreset): QuickSelectionViewModel[] {
    const currentLanguage = this.session.currentLanguage();
    const translate = this.t.bind(this);
    const localizationContext: SportsbookLocalizationContext = { currentLanguage, t: translate };
    const definition = QUICK_SELECTOR_DEFINITIONS[preset.groupId];
    const betType = this.findBetType(match.BetTypes, definition, preset.preferredSpecialValue);
    const lineValue = preset.preferredSpecialValue ?? (betType ? getSpecialValue(betType) : null);
    const marketLabel = betType
      ? resolveBetTypeLabel(match, betType, resolveStaticLabel(preset.marketLabelKey, preset.marketLabel, translate), localizationContext)
      : resolveStaticLabel(preset.marketLabelKey, preset.marketLabel, translate);
    const marketKey = buildSelectionMarketKey(betType, definition.id);
    const selectedOddId = betType ? this.getSelectedOddId(match.Id, betType.Id) : null;

    return definition.columns.map(column => {
      const odd =
        betType?.Odds.find(candidate => candidate.Active && (column.oddKeys as readonly number[]).includes(candidate.Key)) ?? null;

      return {
        key: `${match.Id}:${marketKey}:${column.id}`,
        quickGroupId: preset.groupId,
        betTypeId: betType?.Id ?? null,
        marketKey,
        marketLabel,
        headerLabel: resolveHeaderLabel(column.defaultHeader, column.selectionResolver, lineValue, translate),
        selectionLabel: resolveSelectionLabel(column.selectionResolver, match, lineValue, localizationContext),
        oddId: odd?.Id ?? null,
        value: odd?.Value ?? null,
        available: betType !== null
          && odd !== null
          && isBettableMatch(match)
          && !betType.LimitHit
          && odd.Value > 1,
        selected: odd ? selectedOddId === odd.Id : false
      } satisfies QuickSelectionViewModel;
    });
  }

  private findBetType(
    betTypes: readonly BetType[],
    definition: QuickSelectorDefinition,
    preferredSpecialValue?: number
  ): BetType | null {
    const candidateKeys = definition.candidateBetTypeKeys as readonly number[];
    const columnOddKeys = definition.columns.flatMap(column => column.oddKeys) as readonly number[];
    const activeCandidates = betTypes
      .filter(betType => betType.Active && candidateKeys.includes(betType.Key))
      .filter(betType => betType.Odds.some(odd => odd.Active && columnOddKeys.includes(odd.Key)))
      .sort((left, right) => candidateKeys.indexOf(left.Key) - candidateKeys.indexOf(right.Key));

    if (!activeCandidates.length) {
      return null;
    }

    if (preferredSpecialValue === undefined) {
      return activeCandidates[0];
    }

    return activeCandidates.find(candidate => {
      const specialValue = getSpecialValue(candidate);
      return specialValue !== null && Math.abs(specialValue - preferredSpecialValue) < 0.001;
    }) ?? null;
  }

  private findStoredOdd(match: Match, betTypeId: number, oddId: number): StoredOddProjection | null {
    const betType = match.BetTypes.find(candidate => candidate.Id === betTypeId && candidate.Active);
    if (!betType) {
      return null;
    }

    const odd = betType.Odds.find(candidate => candidate.Id === oddId && candidate.Active);
    return odd ? { betType, odd } : null;
  }

  private resolveBetSlipSelection(
    match: Match,
    storedSelection: StoredSelection,
    currentLanguage: string
  ): ResolvedBetSlipSelection | null {
    const projection = this.findStoredOdd(match, storedSelection.betTypeId, storedSelection.oddId);
    if (!projection) {
      return null;
    }

    const currentSelection = this.resolveLocalizedSelectionProjection(match, projection, currentLanguage);
    if (!currentSelection) {
      return null;
    }

    return {
      tournamentId: match.LB_TournamentID,
      matchId: match.Id,
      matchName: buildMatchName(match, currentLanguage),
      competitors: buildCompetitorsName(match, currentLanguage),
      matchStartDate: match.Date,
      matchIdentifier: match.MatchIdentifier,
      matchNumber: match.MatchNumber,
      sportId: match.Sport,
      matchActive: isBettableMatch(match),
      isLive: isLiveMatch(match),
      isOutright: match.Outright,
      minCombination: match.MinCombination,
      isH2H: match.IsH2H,
      homeTeamBrUniqueId: match.HometeamBrUniqueID,
      awayTeamBrUniqueId: match.AwayteamBrUniqueID,
      key: storedSelectionKey(match.Id, storedSelection.betTypeId),
      betTypeId: storedSelection.betTypeId,
      betTypeKey: projection.betType.Key,
      marketLabel: currentSelection.marketLabel,
      selectionLabel: currentSelection.selectionLabel,
      oddId: storedSelection.oddId,
      oddTypeKey: projection.odd.Key,
      oddFieldType: projection.odd.OddFieldType,
      specialOddsValue: projection.betType.SpecialOddsValue,
      odds: currentSelection.value,
      active: isBettableMatch(match) && !projection.betType.LimitHit
    };
  }

  private resolveLocalizedSelectionProjection(
    match: Match,
    projection: StoredOddProjection,
    currentLanguage: string
  ): ResolvedSelectionProjection | null {
    const translate = this.t.bind(this);
    const localizationContext: SportsbookLocalizationContext = { currentLanguage, t: translate };
    const selectionLabel = resolveDetailOddLabel(match, projection.betType, projection.odd, localizationContext);

    if (!selectionLabel) {
      return null;
    }

    const family = MATCH_DETAIL_SECTION_FAMILIES.find(candidate => candidate.candidateBetTypeKeys.includes(projection.betType.Key));
    const marketFallback = family
      ? localizeSectionTitle(family.id, family.title, translate)
      : resolveCanonicalBetTypeLabel(projection.betType.Key, formatBetTypeKeyFallback(projection.betType.Key), translate);

    return {
      value: projection.odd.Value,
      marketLabel: this.resolveSelectionMarketLabel(match, projection.betType, localizationContext, marketFallback),
      selectionLabel
    };
  }

  private resolveSelectionMarketLabel(
    match: Match,
    betType: BetType,
    localizationContext: SportsbookLocalizationContext,
    fallback: string
  ): string {
    return this.resolveQuickSelectorMarketLabel(match, betType, localizationContext)
      ?? resolveBetTypeLabel(match, betType, fallback, localizationContext);
  }

  private resolveQuickSelectorMarketLabel(
    match: Match,
    betType: BetType,
    localizationContext: SportsbookLocalizationContext
  ): string | null {
    const preset = getQuickSelectorPreset(match.Sport, isLiveMatch(match)).find(candidate => {
      return (QUICK_SELECTOR_DEFINITIONS[candidate.groupId].candidateBetTypeKeys as readonly number[]).includes(betType.Key);
    });

    if (!preset) {
      return null;
    }

    const fallbackLabel = resolveStaticLabel(preset.marketLabelKey, preset.marketLabel, localizationContext.t);
    return resolveBetTypeLabel(match, betType, fallbackLabel, localizationContext);
  }

  private buildMatchDetailSections(match: Match): MatchDetailSectionViewModel[] {
    const listedBetTypes = match.BetTypes.filter(betType => betType.Odds.length > 0);
    const sections: MatchDetailSectionViewModel[] = [];
    const configuredBetTypeKeys = new Set(
      MATCH_DETAIL_SECTION_FAMILIES.flatMap(family => [...family.candidateBetTypeKeys])
    );

    for (const family of MATCH_DETAIL_SECTION_FAMILIES) {
      const familyBetTypes = listedBetTypes
        .filter(betType => family.candidateBetTypeKeys.includes(betType.Key))
        .sort(compareBetTypes);

      for (const betType of familyBetTypes) {
        const section = this.buildMatchDetailSection(match, family.id, family.title, family.layout, betType);
        if (section) {
          sections.push(section);
        }
      }
    }

    const unconfiguredBetTypes = shouldIncludeUnconfiguredMatchDetailBetTypes(match.Sport)
      ? listedBetTypes
        .filter(betType => !configuredBetTypeKeys.has(betType.Key))
        .sort(compareBetTypes)
      : [];

    for (const betType of unconfiguredBetTypes) {
      const section = this.buildMatchDetailSection(match, 'unconfigured-markets', 'Other', 'list', betType, true);
      if (section) {
        sections.push(section);
      }
    }

    return sections;
  }

  private buildMatchDetailSection(
    match: Match,
    sectionId: string,
    title: string,
    layout: MatchDetailSectionViewModel['layout'],
    betType: BetType,
    useBetTypeLabelFallback = false
  ): MatchDetailSectionViewModel | null {
    const translate = this.t.bind(this);
    const localizedTitle = localizeSectionTitle(sectionId, title, translate);
    const options = this.buildMatchDetailOptions(
      match,
      useBetTypeLabelFallback ? formatBetTypeKeyFallback(betType.Key) : localizedTitle,
      betType
    );

    if (!options.length) {
      return null;
    }

    return {
      id: buildDetailMarketKey(betType),
      title: buildMatchDetailMarketTitle(options[0].marketLabel || localizedTitle, betType),
      layout: resolveMatchDetailLayout(layout, options.length),
      defaultExpanded: true,
      tabIds: getMatchDetailTabIdsForBetTypeKey(betType.Key),
      options
    };
  }

  private buildMatchDetailOptions(match: Match, marketLabel: string, betType: BetType): MatchDetailOptionViewModel[] {
    const currentLanguage = this.session.currentLanguage();
    const translate = this.t.bind(this);
    const localizationContext: SportsbookLocalizationContext = { currentLanguage, t: translate };
    const marketKey = buildDetailMarketKey(betType);
    const selectedOddId = this.getSelectedOddId(match.Id, betType.Id);
    const resolvedMarketLabel = resolveBetTypeLabel(match, betType, marketLabel, localizationContext);

    return betType.Odds
      .flatMap(odd => {
        const label = resolveDetailOddLabel(match, betType, odd, localizationContext);
        if (!label) {
          return [];
        }

        return {
          key: `${match.Id}:${marketKey}:${odd.Id}`,
          betTypeId: betType.Id,
          marketKey,
          marketLabel: resolvedMarketLabel,
          selectionLabel: label,
          displayLabel: label,
          oddId: odd.Id,
          value: odd.Value,
          available: isBettableMatch(match) && betType.Active && odd.Active && odd.Value > 1 && !betType.LimitHit,
          selected: selectedOddId === odd.Id
        } satisfies MatchDetailOptionViewModel;
      });
  }

  private matchesCurrentFilter(match: Match): boolean {
    return matchesFilter(match, this.activeFilter(), {
      isDisplayableMatch,
      isLiveMatch
    });
  }

  private matchesCurrentScope(match: Match): boolean {
    return this.visibleSportIds().has(match.Sport) && matchesSearchQuery(match, this.normalizedSearchTokens(), {
      currentLanguage: this.session.currentLanguage(),
      t: this.t.bind(this)
    });
  }

  private matchesCurrentView(match: Match): boolean {
    return this.matchesCurrentScope(match) && this.matchesCurrentFilter(match);
  }

  private hasDisplayableLiveMatches(): boolean {
    for (const match of this.session.matches().values()) {
      if (this.matchesCurrentScope(match) && isDisplayableMatch(match) && isLiveMatch(match)) {
        return true;
      }
    }

    return false;
  }

  private hasStoredSelection(action: SelectionToggleRequest): boolean {
    const match = this.session.matches().get(action.matchId);
    if (!match || !isBettableMatch(match)) {
      return false;
    }

    const projection = this.findStoredOdd(match, action.betTypeId, action.oddId);
    return projection !== null && !projection.betType.LimitHit;
  }

  private getSelectedOddId(matchId: number, betTypeId: number | null): number | null {
    if (betTypeId === null) {
      return null;
    }

    return this.selectedSelections().get(storedSelectionKey(matchId, betTypeId))?.oddId ?? null;
  }

  private getSportOrder(sportId: number): number {
    const index = DEFAULT_SPORT_ORDER.indexOf(sportId);
    return index === -1 ? DEFAULT_SPORT_ORDER.length + sportId : index;
  }

  private t(key: string): string {
    this.translationRevision();
    this.session.currentLanguage();

    const instantValue = this.translate?.instant(key);
    const translated = trimToNull(typeof instantValue === 'string' ? instantValue : null);
    return translated ?? key;
  }

  private resetBetSlipSubmissionState(): void {
    if (this.betSlipSubmissionState().kind === 'idle' && this.betSlipSubmissionState().messageKey === null) {
      return;
    }

    this.betSlipSubmissionState.set(BET_SLIP_IDLE_SUBMISSION_STATE);
  }

  private pulseMaxWin(): void {
    this.maxWinPulseKey.update(current => current + 1);
  }
}

function storedSelectionKey(matchId: number, betTypeId: number): string {
  return `${matchId}:${betTypeId}`;
}

function buildQuickSelectionGroups(
  selections: readonly QuickSelectionViewModel[]
): TournamentGroupViewModel['quickSelectionGroups'] {
  const groups = new Map<string, { id: string; label: string; headerLabels: string[] }>();

  for (const selection of selections) {
    const current = groups.get(selection.quickGroupId);
    if (current) {
      current.headerLabels.push(selection.headerLabel);
      continue;
    }

    groups.set(selection.quickGroupId, {
      id: selection.quickGroupId,
      label: selection.marketLabel,
      headerLabels: [selection.headerLabel]
    });
  }

  return Array.from(groups.values());
}

function buildCompetitorsName(match: Match, currentLanguage: string): string {
  if (match.Outright) {
    return buildMatchName(match, currentLanguage);
  }

  const homeTeam = resolveHomeTeamLabel(match, currentLanguage);
  const awayTeam = resolveAwayTeamLabel(match, currentLanguage);
  return awayTeam ? `${homeTeam} - ${awayTeam}` : homeTeam;
}

function formatBetTypeKeyFallback(betTypeKey: BetTypeKey): string {
  const rawKey = BetTypeKey[betTypeKey];
  return typeof rawKey === 'string'
    ? rawKey
      .toLowerCase()
      .split('_')
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ')
    : '';
}

function buildMatchDetailMarketTitle(marketLabel: string, betType: BetType): string {
  const specialOddsValue = trimToNull(betType.SpecialOddsValue);
  if (!specialOddsValue) {
    return marketLabel;
  }

  const normalizedSpecialOddsValue = specialOddsValue.replace('-', '/');
  return marketLabel.includes(specialOddsValue) || marketLabel.includes(normalizedSpecialOddsValue)
    ? marketLabel
    : `${marketLabel} (${specialOddsValue})`;
}
